import React, { useState } from 'react';

import PartnerLogos from '@hpstellar/core/PartnerLogos';
import Typography from '@hpstellar/core/Typography';
import QuickFilterButton from '@hpstellar/core/QuickFilterButton';
import Button from '@hpstellar/core/Button';
import Carousel from '@hpstellar/core/Carousel';
import withWindowResize from '../ui/components/window-resize';
import { MetricsElement } from 'react-metrics';

import withPage, { loadGraphQLData } from '../page';

import VWAProductList from './components/vwa-product-list';
import VWALeftMenu from './components/vwa-left-menu';
import SortBar from './components/sort-bar';
import usePage from '../hooks/usePage';
import ETRWCSApi from '../core/src/etr-wcs-api-client';
import CompareDrawer from '../compare/components/compare-drawer';
import FilterModal from './components/filter-modal';
import FooterLogo from '../shared/components/footer-logo';
import StickyBar from './components/sticky-bar';

import './vwa.less';

const getFilters = (filters, key, urlValue) => {
    const reqFilters = { ...filters };

    // special price case, behaves a bit different than normal facets
    if (key === 'Price') {
        if (filters[key] && filters[key][urlValue]) {
            delete reqFilters[key];
            return reqFilters;
        } else if (filters[key]) {
            reqFilters[key] = {};
            reqFilters[key][urlValue] = urlValue;
            if (urlValue === '-') delete reqFilters[key];
        } else {
            reqFilters[key] = { [urlValue]: urlValue };
        }
        return reqFilters;
    }

    if (filters[key] && filters[key][urlValue]) {
        delete reqFilters[key][urlValue];
        if (Object.keys(reqFilters[key]).length < 1) delete reqFilters[key];
    } else if (filters[key]) {
        reqFilters[key][urlValue] = urlValue;
    } else {
        reqFilters[key] = { [urlValue]: urlValue };
    }
    return reqFilters;
};

const buildUrlFromFilters = filters => {
    const urlFacets = [];
    Object.keys(filters)
        .sort()
        .forEach(facet => {
            const keys = Object.keys(filters[facet]).sort();
            if (keys.length < 1) return;
            urlFacets.push(`${facet}=${keys.map(key => key).join(',')}`);
        });
    return urlFacets.join(';');
};

const buildFilterFromUrl = url => {
    if (!url) return {};
    const facets = url.split(';');
    const filters = {};
    facets.forEach(facet => {
        const [key, valueString] = facet.split('=');
        const value = valueString.split(',');
        const values = {};
        value.forEach(val => {
            values[val] = val;
        });
        filters[key] = values;
    });
    return filters;
};

const buildParamsFromUrl = url => {
    if (!url) return {};
    const keyVal = url.split('&');
    const params = {};
    keyVal.forEach(item => {
        const [key, val] = item.split('=');
        params[key] = val;
    });
    return params;
};

const buildUrlFromParams = params => {
    if (!params) return '';
    const url = [];
    Object.keys(params)
        .sort()
        .forEach(param => {
            url.push(`${param}=${params[param]}`);
        });
    return url.join('&');
};

const DEFAULT_PAGE_SIZE = 12;

const VWAContainer = ({ slugInfo, width, device, history, location }) => {
    const { quickFilters: filterContainer, finderResults, partnerLogos } = slugInfo?.components;
    const vwaDetails = finderResults?.vwaDetails;
    const title = slugInfo?.seo?.h1Tag || '';

    if (!vwaDetails) return null;

    const [modalState, setModalState] = useState(false);
    const [pageIndex, setPageIndex] = useState(DEFAULT_PAGE_SIZE);
    const [quickFilterActive, setQuickFilterActive] = useState(-1);
    // TODO: change this to also take into account other params like orderBy and paging
    const [slug, path] = location.pathname.split('vwa/').pop().split('/');

    const { updateComponent, updateSeo } = usePage();
    const [filters, setFilters] = useState(buildFilterFromUrl(path));
    const [params, setParams] = useState(buildParamsFromUrl(location?.search));
    const [presetRange, setPresetRange] = useState(-1);
    const filterCount = Object.keys(filters).length;

    const update = (updatedFilters, updatedParams) => {
        const updatedPath = buildUrlFromFilters(updatedFilters);
        ETRWCSApi.component
            .get('vwa', 'finder-results', {
                path: `vwa/${slug}${updatedPath ? `/${updatedPath}` : ''}`,
                ...params,
                ...updatedParams,
            })
            .then(resp => {
                updateComponent(
                    {
                        finderResults: resp,
                    },
                    state => {
                        const { components } = state;
                        return { components: { ...components, finderResults: resp } };
                    }
                );
                const paramUrl = buildUrlFromParams({ ...params, ...updatedParams });
                history.push(
                    `/us-en/shop/vwa/${slug}${updatedPath ? `/${updatedPath}` : ''}${paramUrl ? `?${paramUrl}` : ''}`
                );
                setFilters(updatedFilters);

                ETRWCSApi.seo.get('vwa', `vwa/${slug}${updatedPath ? `/${updatedPath}` : ''}`, params).then(resp => {
                    const seoData = resp?.seoData || [];
                    if (seoData.length < 1) return;

                    updateSeo(seoData[0], (state, seo) => {
                        return { ...state, seo };
                    });
                });
            });
    };

    const onSelectFacet = (key, urlValue) => () => {
        // const slug = document.location.pathname.split('vwa/').pop().split('/')[0];
        const reqFilters = getFilters(filters, key, urlValue);
        update(reqFilters);
        setPageIndex(DEFAULT_PAGE_SIZE);
    };

    const onSort = ({ value }) => {
        setParams({ orderBy: value });
        update({ ...filters }, { orderBy: value });
        setPageIndex(DEFAULT_PAGE_SIZE);
    };

    const clear = () => {
        update({});
        setPresetRange(-1);
        setQuickFilterActive(-1);
        setPageIndex(DEFAULT_PAGE_SIZE);
    };

    const setQuickFilter = (value, i) => {
        if (i === quickFilterActive) {
            setQuickFilterActive(-1);
            update({});
        } else {
            const quickFilter = buildFilterFromUrl(value);
            setQuickFilterActive(i);
            update(quickFilter);
        }
        setPageIndex(DEFAULT_PAGE_SIZE);
        setPresetRange(-1);
    };

    const loadMore = () => {
        const updatedPath = buildUrlFromFilters(filters);
        ETRWCSApi.component
            .get('vwa', 'finder-results', {
                path: `vwa/${slug}${updatedPath ? `/${updatedPath}` : ''}`,
                beginIndex: pageIndex,
                ...params,
            })
            .then(resp => {
                updateComponent(
                    {
                        finderResults: resp,
                    },
                    state => {
                        const { components } = state;
                        return {
                            components: {
                                ...components,
                                finderResults: {
                                    ...resp,
                                    vwaDetails: {
                                        ...resp.vwaDetails,
                                        products: [
                                            ...components.finderResults.vwaDetails.products,
                                            ...resp.vwaDetails.products,
                                        ],
                                    },
                                },
                            },
                        };
                    }
                );
                setPageIndex(pageIndex + DEFAULT_PAGE_SIZE);
            });
    };

    const { products, sortValues, totalProducts, beginIndex } = vwaDetails;
    const { quickFilters = [], subTitle } = filterContainer || {};

    const displayLoadMore = parseInt(totalProducts, 10) - parseInt(beginIndex, 10) > 12;
    const isMobile = width ? width < 768 : device === 'mobile';

    const mobileFilterButton = (
        <div className="mobileFilterButtonContainer">
            <Button
                className="mobileFilterButton"
                variation="secondary"
                color="dark"
                onClick={() => setModalState(true)}
            >
                Sort & Filter
                {filterCount > 0 && ` (${filterCount})`}
            </Button>
        </div>
    );

    const sortBar = (
        <SortBar
            filterCount={filterCount}
            clear={clear}
            vwaDetails={vwaDetails}
            sortValues={sortValues}
            onSort={onSort}
        />
    );

    const wrapCarousel = comp => {
        const settings = {
            slidesToShow: 3,
            slidesToScroll: 1,
            arrows: false,
            dots: true,
            infinite: false,
            responsive: [
                {
                    breakpoint: 768,
                    settings: {
                        slidesToShow: 3.5,
                    },
                },
                {
                    breakpoint: 600,
                    settings: {
                        slidesToShow: 3,
                    },
                },
                {
                    breakpoint: 500,
                    settings: {
                        slidesToShow: 2.5,
                    },
                },
                {
                    breakpoint: 375,
                    settings: {
                        slidesToShow: 2,
                    },
                },
            ],
        };
        return isMobile ? (
            <Carousel className="filterCarousel" style={{ maxWidth: '100vw' }} {...settings}>
                {comp}
            </Carousel>
        ) : (
            <div className="quick-filter-container ">{comp}</div>
        );
    };
    return (
        <>
            <div className="vwa-page">
                <div className="heading">
                    <div className="content">
                        <Typography className="headingTitle" variant="headingTitleMedium" tag="h1">
                            {title.toUpperCase()}
                        </Typography>
                        <div className="quickFilters">
                            <p className="quickFilterTitle">{subTitle}</p>
                            {wrapCarousel(
                                quickFilters.map((f, i) => (
                                    <MetricsElement
                                        key={i}
                                        element="span"
                                        data-metrics-event-name="searchFilter"
                                        data-metrics-event="e_facet"
                                        data-metrics-filter-checked={true}
                                        data-metrics-filter-categories="quick"
                                        data-metrics-filter-value={f.label.toLowerCase().replace(/\s+/gi, '')}
                                    >
                                        <QuickFilterButton
                                            className="quickFilter"
                                            onClick={() => setQuickFilter(f.href, i)}
                                            isActive={i === quickFilterActive}
                                        >
                                            {f.label}
                                        </QuickFilterButton>
                                    </MetricsElement>
                                ))
                            )}
                        </div>
                    </div>
                    <PartnerLogos {...(partnerLogos || {})} className="logo" />
                </div>

                <StickyBar id="vwaStickyBar" height={isMobile ? '84px' : '60px'}>
                    {isMobile ? mobileFilterButton : sortBar}
                </StickyBar>
                {isMobile && sortBar}

                <div className="content">
                    {!isMobile && (
                        <VWALeftMenu
                            facets={vwaDetails.facets}
                            expandedFacets={vwaDetails.expandedFacets}
                            onSelectFacet={onSelectFacet}
                            vanityUrl={slugInfo?.vanityUrl}
                            filters={filters}
                            presetRange={presetRange}
                            setPresetRange={setPresetRange}
                        />
                    )}
                    <VWAProductList products={products}>
                        {displayLoadMore && (
                            <div className="loadMore">
                                <Button variation="secondary" icon="add" onClick={loadMore}>
                                    Load more
                                </Button>
                            </div>
                        )}
                    </VWAProductList>
                </div>

                {isMobile && (
                    <FilterModal
                        isOpen={modalState}
                        filterCount={filterCount}
                        resultsCount={vwaDetails.totalProducts}
                        onModalClose={() => setModalState(false)}
                        clearFilters={clear}
                    >
                        <VWALeftMenu
                            facets={vwaDetails.facets}
                            expandedFacets={vwaDetails.expandedFacets}
                            onSelectFacet={onSelectFacet}
                            aligned="right"
                            sortValues={sortValues}
                            sortValue={vwaDetails.orderBy}
                            onSort={onSort}
                            vanityUrl={slugInfo?.vanityUrl}
                            filters={filters}
                            presetRange={presetRange}
                            setPresetRange={setPresetRange}
                        />
                    </FilterModal>
                )}
            </div>
            <FooterLogo logos={partnerLogos} breakpoint={1024} />
            <CompareDrawer />
        </>
    );
};

export default {
    component: withPage(withWindowResize(VWAContainer), {
        wrapperClassName: 'vwa-root',
        showWordCloud: true,
        useV2: true,
        useGraphQL: true,
    }),
    loadData: loadGraphQLData,
};
